import React, { useState, useEffect } from 'react';
import { FovModal, FovSelect, FovOption, FovToggle, VerticalSpacer } from 'components';
import {
  ModelSelectWrapper,
  SimpleFieldLabel,
  SimpleFieldInput,
  SimpleToggleRow,
  SimpleToggleList,
  GenerateButton,
} from '../imageCanvasStyles';
import {
  MESHY_AI_MODELS,
  MESHY_MODEL_TYPES,
  MESHY_TOPOLOGIES,
  MESHY_POSE_MODES,
  MESHY_POLYCOUNT_MIN,
  meshyPolycountMax,
  validateMeshySettings,
} from '../meshySettings';

const POSE_MODE_LABELS = { '': 'None', 'a-pose': 'A-Pose', 't-pose': 'T-Pose' };

const isMeshy6OrLatest = aiModel => aiModel === 'meshy-6' || aiModel === 'latest';

const clampPolycount = (value, modelType) => Math.min(
  meshyPolycountMax(modelType),
  Math.max(MESHY_POLYCOUNT_MIN, Number(value) || MESHY_POLYCOUNT_MIN),
);

const MeshySettingsModal = ({ theme, selectedTheme, visible, settings, onCancel, onSave }) => {
  const shared = { theme, $selectedTheme: selectedTheme };
  const [ draft, setDraft ] = useState(settings);

  useEffect(() => {
    if (visible) setDraft(settings);
  }, [ visible, settings ]);

  const handleModelTypeChange = value => {
    setDraft({ ...draft, modelType: value, targetPolycount: clampPolycount(draft.targetPolycount, value) });
  };

  const handlePolycountChange = e => {
    setDraft({ ...draft, targetPolycount: e.target.value });
  };

  const handlePolycountBlur = () => {
    setDraft({ ...draft, targetPolycount: clampPolycount(draft.targetPolycount, draft.modelType) });
  };

  const handleSave = () => {
    const { valid } = validateMeshySettings(draft);
    if (!valid) return;
    onSave(draft);
  };

  const supportsAdvanced = isMeshy6OrLatest(draft.aiModel);
  const { shouldTexture } = draft;

  return (
    <FovModal title='Meshy AI Settings' visible={visible} onClose={onCancel} width={360}>
      <SimpleFieldLabel {...shared}>AI Model</SimpleFieldLabel>
      <ModelSelectWrapper>
        <FovSelect
          value={draft.aiModel}
          placeholder={draft.aiModel}
          onChange={(e, newVal) => setDraft({ ...draft, aiModel: newVal })}
        >
          {MESHY_AI_MODELS.map(value => (
            <FovOption key={value} value={value}>{value}</FovOption>
          ))}
        </FovSelect>
      </ModelSelectWrapper>

      <SimpleFieldLabel {...shared}>Model Type</SimpleFieldLabel>
      <ModelSelectWrapper>
        <FovSelect
          value={draft.modelType}
          placeholder={draft.modelType}
          onChange={(e, newVal) => handleModelTypeChange(newVal)}
        >
          {MESHY_MODEL_TYPES.map(value => (
            <FovOption key={value} value={value}>{value}</FovOption>
          ))}
        </FovSelect>
      </ModelSelectWrapper>

      <SimpleFieldLabel {...shared}>Topology</SimpleFieldLabel>
      <ModelSelectWrapper>
        <FovSelect
          value={draft.topology}
          placeholder={draft.topology}
          onChange={(e, newVal) => setDraft({ ...draft, topology: newVal })}
        >
          {MESHY_TOPOLOGIES.map(value => (
            <FovOption key={value} value={value}>{value}</FovOption>
          ))}
        </FovSelect>
      </ModelSelectWrapper>

      <SimpleFieldLabel {...shared}>Target Polycount</SimpleFieldLabel>
      <SimpleFieldInput
        {...shared}
        type='number'
        min={MESHY_POLYCOUNT_MIN}
        max={meshyPolycountMax(draft.modelType)}
        value={draft.targetPolycount}
        onChange={handlePolycountChange}
        onBlur={handlePolycountBlur}
      />

      <SimpleFieldLabel {...shared}>Pose Mode</SimpleFieldLabel>
      <ModelSelectWrapper>
        <FovSelect
          value={draft.poseMode}
          placeholder={POSE_MODE_LABELS[draft.poseMode]}
          onChange={(e, newVal) => setDraft({ ...draft, poseMode: newVal })}
        >
          {MESHY_POSE_MODES.map(value => (
            <FovOption key={value || 'none'} value={value}>{POSE_MODE_LABELS[value]}</FovOption>
          ))}
        </FovSelect>
      </ModelSelectWrapper>

      {shouldTexture && (
        <>
          <SimpleFieldLabel {...shared}>Texture Prompt</SimpleFieldLabel>
          <SimpleFieldInput
            {...shared}
            type='text'
            placeholder='Optional texture description'
            value={draft.texturePrompt}
            onChange={e => setDraft({ ...draft, texturePrompt: e.target.value })}
          />
        </>
      )}

      <VerticalSpacer size={15} />

      <SimpleToggleList>
        <SimpleToggleRow {...shared}>
          Should Remesh

          <FovToggle
            name='shouldRemeshToggle'
            checked={!!draft.shouldRemesh}
            callback={value => setDraft({ ...draft, shouldRemesh: value })}
          />
        </SimpleToggleRow>

        <SimpleToggleRow {...shared}>
          Should Texture

          <FovToggle
            name='shouldTextureToggle'
            checked={!!draft.shouldTexture}
            callback={value => setDraft({ ...draft, shouldTexture: value })}
          />
        </SimpleToggleRow>

        <SimpleToggleRow {...shared}>
          Enable PBR

          <FovToggle
            name='enablePBRToggle'
            checked={!!draft.enablePBR}
            disabled={!shouldTexture}
            callback={value => setDraft({ ...draft, enablePBR: value })}
          />
        </SimpleToggleRow>

        <SimpleToggleRow {...shared}>
          HD Texture

          <FovToggle
            name='hdTextureToggle'
            checked={!!draft.hdTexture}
            disabled={!shouldTexture || !supportsAdvanced}
            callback={value => setDraft({ ...draft, hdTexture: value })}
          />
        </SimpleToggleRow>

        <SimpleToggleRow {...shared}>
          Image Enhancement

          <FovToggle
            name='imageEnhancementToggle'
            checked={!!draft.imageEnhancement}
            disabled={!supportsAdvanced}
            callback={value => setDraft({ ...draft, imageEnhancement: value })}
          />
        </SimpleToggleRow>

        <SimpleToggleRow {...shared}>
          Remove Lighting

          <FovToggle
            name='removeLightingToggle'
            checked={!!draft.removeLighting}
            disabled={!supportsAdvanced}
            callback={value => setDraft({ ...draft, removeLighting: value })}
          />
        </SimpleToggleRow>

        <SimpleToggleRow {...shared}>
          Moderation

          <FovToggle
            name='moderationToggle'
            checked={!!draft.moderation}
            callback={value => setDraft({ ...draft, moderation: value })}
          />
        </SimpleToggleRow>
      </SimpleToggleList>

      <VerticalSpacer size={20} />

      <GenerateButton {...shared} onClick={handleSave}>
        Save
      </GenerateButton>
    </FovModal>
  );
};

export { MeshySettingsModal };
